// @flow
import _ from 'lodash'
import React, { Component } from 'react'
import { translations, translateFunc } from '../../shared/localize'
import ListItem from './ListItem'
import styled from 'styled-components'
import type { Segment, Contact, Lookalike, ProviderTargetingSource } from '../types'

const Label = styled.div`
  font-weight: bold;
  width: 100px;
  font-size: 14px;
  flex-basis: content;
`
const ListWrapper = styled.div`
  padding: 10px 0;
  display: flex;
  align-items: center;
`
const Items = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 480px;
`
type Props = {|
  segments: Array<Segment>,
  contacts: Array<Contact>,
  lookalikeSegments: Array<Lookalike>,
  lookalikeContacts: Array<Lookalike>,
  providerTargetingSources: Array<ProviderTargetingSource>,
  includedSegmentIds: Array<number>,
  excludedSegmentIds: Array<number>,
  includedContactIds: Array<number>,
  excludedContactIds: Array<number>,
  includedLookalikeSegmentIds: Array<number>,
  includedLookalikeContactIds: Array<number>,
  includedProviderTargetingSourceIds: Array<number>,
  excludedProviderTargetingSourceIds: Array<number>,
  onIncludedSegmentRemove: Function,
  onExcludedSegmentRemove: Function,
  onIncludedContactRemove: Function,
  onExcludedContactRemove: Function,
  onIncludedLookalikeSegmentRemove: Function,
  onIncludedLookalikeContactRemove: Function,
  onIncludedProviderTargetingSourceRemove: Function,
  onExcludedProviderTargetingSourceRemove: Function
|}

const t = translateFunc(translations.targeting_sources)

export default class List extends Component<Props> {
  render() {
    return (
      <div>
        {!_.isEmpty(this.includedItems()) && (
          <ListWrapper>
            <Label>{t('included')}</Label>
            <Items>{this.includedItems().map((item, index) => [index !== 0 && ',', item])}</Items>
          </ListWrapper>
        )}
        {!_.isEmpty(this.excludedItems()) && (
          <ListWrapper>
            <Label>{t('excluded')}</Label>
            <Items>{this.excludedItems().map((item, index) => [index !== 0 && ',', item])}</Items>
          </ListWrapper>
        )}
      </div>
    )
  }

  includedItems(): Array<any> {
    const {
      includedSegmentIds,
      segments,
      onIncludedSegmentRemove,
      contacts,
      onIncludedContactRemove,
      includedContactIds,
      lookalikeSegments,
      onIncludedLookalikeSegmentRemove,
      includedLookalikeSegmentIds,
      lookalikeContacts,
      onIncludedLookalikeContactRemove,
      includedLookalikeContactIds,
      providerTargetingSources,
      onIncludedProviderTargetingSourceRemove,
      includedProviderTargetingSourceIds
    } = this.props
    const includedSegments: Array<any> = _.compact(
      _.map(includedSegmentIds, (id: number) => {
        const segment = _.find(segments, (segment) => segment.id === id)
        return (
          segment && (
            <ListItem
              key={'segment' + id}
              name={segment.name}
              hint={segment.substrings}
              path={segment.path}
              onClose={() => onIncludedSegmentRemove(id)}
            />
          )
        )
      })
    )
    const includedContacts: Array<any> = _.compact(
      _.map(includedContactIds, (id: number) => {
        const contact = _.find(contacts, (contact) => contact.id === id)
        return (
          contact && (
            <ListItem
              key={'contact' + id}
              name={contact.name}
              hint={t('contact_list')}
              onClose={() => onIncludedContactRemove(id)}
            />
          )
        )
      })
    )
    const includedLookalikeSegments: Array<any> = _.compact(
      _.map(includedLookalikeSegmentIds, (id: number) => {
        const lookalikeSegment = _.find(
          lookalikeSegments,
          (lookalikeSegment) => lookalikeSegment.id === id
        )
        return (
          lookalikeSegment && (
            <ListItem
              key={'lookalike_segment' + id}
              name={lookalikeSegment.name}
              hint={t('lookalike_segment')}
              onClose={() => onIncludedLookalikeSegmentRemove(id)}
            />
          )
        )
      })
    )
    const includedLookalikeContacts: Array<any> = _.compact(
      _.map(includedLookalikeContactIds, (id: number) => {
        const lookalikeContact = _.find(
          lookalikeContacts,
          (lookalikeContact) => lookalikeContact.id === id
        )
        return (
          lookalikeContact && (
            <ListItem
              key={'lookalike_contact' + id}
              name={lookalikeContact.name}
              hint={t('lookalike_contact')}
              onClose={() => onIncludedLookalikeContactRemove(id)}
            />
          )
        )
      })
    )
    const includedProviderTargetingSources: Array<any> = _.compact(
      _.map(includedProviderTargetingSourceIds, (id: number) => {
        const providerTargetingSource = _.find(
          providerTargetingSources,
          (source) => source.id === id
        )
        return (
          providerTargetingSource && (
            <ListItem
              key={'provider_targeting_source' + id}
              name={providerTargetingSource.name}
              hint={t('provider_targeting_source')}
              onClose={() => onIncludedProviderTargetingSourceRemove(id)}
            />
          )
        )
      })
    )
    return _.concat(
      includedSegments,
      includedContacts,
      includedLookalikeSegments,
      includedLookalikeContacts,
      includedProviderTargetingSources
    )
  }

  excludedItems(): Array<any> {
    const {
      excludedSegmentIds,
      segments,
      onExcludedSegmentRemove,
      contacts,
      onExcludedContactRemove,
      excludedContactIds,
      providerTargetingSources,
      onExcludedProviderTargetingSourceRemove,
      excludedProviderTargetingSourceIds
    } = this.props
    const excludedSegments: Array<any> = _.compact(
      _.map(excludedSegmentIds, (id: number) => {
        const segment = _.find(segments, (segment) => segment.id === id)
        return (
          segment && (
            <ListItem
              key={'segment' + id}
              name={segment.name}
              hint={segment.substrings}
              path={segment.path}
              onClose={() => onExcludedSegmentRemove(id)}
            />
          )
        )
      })
    )
    const excludedContacts: Array<any> = _.compact(
      _.map(excludedContactIds, (id: number) => {
        const contact = _.find(contacts, (contact) => contact.id === id)
        return (
          contact && (
            <ListItem
              key={'contact' + id}
              name={contact.name}
              hint={t('contact_list')}
              onClose={() => onExcludedContactRemove(id)}
            />
          )
        )
      })
    )
    const excludedProviderTargetingSources: Array<any> = _.compact(
      _.map(excludedProviderTargetingSourceIds, (id: number) => {
        const providerTargetingSource = _.find(
          providerTargetingSources,
          (source) => source.id === id
        )
        return (
          providerTargetingSource && (
            <ListItem
              key={'provider_targeting_source' + id}
              name={providerTargetingSource.name}
              hint={t('provider_targeting_source')}
              onClose={() => onExcludedProviderTargetingSourceRemove(id)}
            />
          )
        )
      })
    )

    return _.concat(excludedSegments, excludedContacts, excludedProviderTargetingSources)
  }
}
